import React from 'react';
import { notification } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  HomeOutlined,
  SettingOutlined,
  LoadingOutlined,
  FundViewOutlined,
  TableOutlined,
  BarChartOutlined,
  LineChartOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import { GetDecryptedString } from './encryption';

//system ID
export const systemID = '003';

//時間區間
export const DEFAULT_TIMEZONE = '8';

//刷新時間
export const refetchInterval = 1000 * 60 * 5;

// loading spin
export const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 55,
    }}
    spin
  />
);

// menu
export const menu = [
  {
    key: '/customerlist',
    icon: <ReconciliationOutlined />,
    label: <Link to="/customerlist">綠電與儲能整合評估</Link>,
    name: '綠電與儲能整合評估',
    path: '/customerlist',
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: <Link to="/settings">環境設定</Link>,
    name: '環境設定',
    path: '/settings',
  },
]

//路由
export const routeList = [
  {
    path: '/',
    breadcrumbName: (
      <>
        <HomeOutlined />{" "}
        <span>微電能源</span>
      </>
    ),
  },
  {
    path: '/customerlist',
    breadcrumbName: '綠電與儲能整合評估',
  },
  {
    path: '/customerlist/:id',
    breadcrumbName: '客戶詳情',
  },
  {
    path: '/customerlist/:id/addevaluation',
    breadcrumbName: '新增評估',
  },
  {
    path: '/settings',
    breadcrumbName: '環境設定',
  },
];

//抓取token
export const getToken = () => {
  return GetDecryptedString(window.localStorage.getItem('token'));
};

//抓取refreshToken
export const getRefreshToken = () => {
  return GetDecryptedString(window.localStorage.getItem('refreshToken'));
};

//麵包屑計算路由
export const itemRender = (route, params, routes) => {
  const last = routes.indexOf(route) === routes.length - 1;
  return last ? <span>{route.breadcrumbName}</span> : <Link to={route.path}>{route.breadcrumbName}</Link>;
};

export const getTimelinePeriod = (timeline = 'day') => {
  if (timeline === 'day' || timeline === 'week') {
    return 'quarter';
  } else if (timeline === 'month') {
    return 'daily';
  } else if (timeline === 'year') {
    return 'monthly';
  } else {
    return 'yearly';
  }
};

export const getTimelinePeriodForTarget = (timeline = 'day') => {
  if (timeline === 'day' || timeline === 'week') {
    return 'daily';
  } else if (timeline === 'month') {
    return 'monthly';
  } else if (timeline === 'year') {
    return 'yearly';
  } else {
    return 'yearly';
  }
};

export const dateOptions = [
  {
    label: '天',
    value: 'day',
  },
  {
    label: '週',
    value: 'week',
  },
  {
    label: '月',
    value: 'month',
  },
  {
    label: '年',
    value: 'year',
  },
  {
    label: '累積至今',
    value: 'accumulatedToNow',
  },
];

//計算累積值
export const calculateCumulationValue = (data = [], key = 'value') => {
  if (data?.length === 0 || data === null) return 0;
  const cumulationValue = data?.reduce?.(
    (accumulator, currentValue) => accumulator + (currentValue?.[key] || 0),
    0
  );
  return Number(cumulationValue?.toFixed(2));
};

export const defaultAnalysisMenu = [
  {
    key: 'energyFlow',
    label: '能源流動',
    type: 'group',
    children: [
      {
        key: 'energyControl',
        icon: <FundViewOutlined />,
        label: '能源調度',
      },
      {
        key: 'energyReview',
        icon: <TableOutlined />,
        label: '能源使用總覽',
      },
      {
        key: 'contractStatus',
        icon: <TableOutlined />,
        label: '契約容量狀態',
      },
      {
        key: 'energyTarget',
        icon: <TableOutlined />,
        label: '能源調度指標',
      },
      {
        key: 'carbonEmissions',
        icon: <BarChartOutlined />,
        label: '減少的碳排放量',
      },
    ],
  },
  {
    key: 'energyDevice',
    label: '能源設備',
    type: 'group',
    children: [
      {
        key: 'solarMonitor',
        icon: <LineChartOutlined />,
        label: '太陽能設備',
      },
      {
        key: 'loadMonitor',
        icon: <LineChartOutlined />,
        label: '負載設備',
      },
      {
        key: 'essMonitor',
        icon: <LineChartOutlined />,
        label: '儲能設備',
      },
      {
        key: 'evseMonitor',
        icon: <LineChartOutlined />,
        label: '充電樁設備',
        name: '充電樁設備',
      },
      {
        key: 'windMonitor',
        icon: <LineChartOutlined />,
        label: '風能設備',
      },
    ],
  },
  {
    key: 'economyTarget',
    label: '經濟指標',
    type: 'group',
    children: [
      {
        key: 'greenBenefits',
        icon: <BarChartOutlined />,
        label: '綠電節電效益',
      },
      {
        key: 'essBenefits',
        icon: <BarChartOutlined />,
        label: '儲能節電效益',
      },
      {
        key: 'economyEstimate',
        icon: <BarChartOutlined />,
        label: '經濟效益預估',
      },
    ],
  },
];

//token過期時間 (30分)
export const expiresIn = 30 * 60 * 1000;

//編碼網址參數
export const codeParams = (data) => {
  let queryString = '';

  // 遍历数组，为每个对象构建查询字符串
  data.forEach((obj, index) => {
    Object.keys(obj).forEach((key) => {
      queryString += `conditions[${index}][${key}]=${encodeURIComponent(obj[key])}&`;
    });

    return queryString;
  });

  // 移除最后一个多余的"&"
  return queryString.slice(0, -1);
};

//解析網址參數
export const decodeParams = (queryString) => {
  // 解析查询字符串
  const params = new URLSearchParams(queryString);

  // 初始化结果数组
  const result = [];

  // 遍历所有参数
  for (const [key, value] of params) {
    // 使用正则表达式匹配索引和字段名
    const match = key.match(/conditions\[(\d+)\]\[(\w+)\]/);
    if (match) {
      const index = parseInt(match[1], 10);
      const field = match[2];

      // 确保数组长度
      while (index >= result.length) {
        result.push({});
      }

      // 添加值到对应的对象中
      result[index][field] = value;
    }
  }
  return result;
};

//清除使用者資訊
export const sessionClear = () => {
  window.localStorage.removeItem('user');
  window.localStorage.removeItem('token');
};

//跳出錯誤訊息
export const openNotification = (placement, error, key) => {
  notification.warning({
      message: error,
      placement,
      key,
  })
}

const valueToLabelMap = {
  gray: '市電',
  selfPv: '自發自用太陽能',
  wheeling: '綠電轉供',
  green: '綠電',
  empty: '市電',
  addWheeling: '購買綠電',
  certificate: '綠電憑證',
  curtailment: '棄光',
}

export const renderColorfulLegendText = (value, data) => {
  const label = valueToLabelMap[value] || '未定義'
  const percent = data.find(i=>i.name === value).percent
  return (
      <span className="recharts-legend-text">
        {label} ({percent})
      </span>
  )
}

//計算所占百分比
export const calculatePercentages = (data) => {
  if (!data || data?.length === 0) return;
  // 1. 計算所有 value 的總和
  const total = data?.reduce((sum, item) => sum + item.value, 0);

  // 2. 生成每項佔比的百分比
  return data.map(item => ({
    name: item.name,
    value: item.value,
    percent: total !== 0 ? ((item.value / total) * 100).toFixed(2) + "%" : "0%"
  }));
}

// renderTipFormatter
export const renderTipFormatter = (value, name) => {
  const formatValue = Number(value?.toFixed(2) || 0) + ' kWh'
  return [formatValue, valueToLabelMap[name] || '未定義']
}

export const addUnitHandler = ({data, unit}) => {
  if (data === '' || data === null || data === undefined) return '-';
  
  // 確保 data 是數字
  data = Number(data);
  if (isNaN(data)) return '-';

  if (unit === '%') {
    data = Number((data * 100).toFixed(2));
  } else if (unit === 'NTD' || unit === 'kWh') {
    if (data >= 10000) {
      data = Number((data / 10000).toFixed(1)) + ' 萬'; // 超过万元，转换为万元
    } else {
      data = data.toLocaleString('en-US'); // 不满万元，直接显示
    }
  } else {
    data = data.toLocaleString('en-US'); // 为非百分比的数值添加千分位分隔符
  }
  
  return data;
}

// 判斷字串是否為整數，如果不是則除以 100
export const formatStringNumber = (strNum) => {
  const number = parseFloat(strNum) / 100; // 先將字串除以 100
  return number % 1 === 0 ? number.toFixed(1) : Number(number.toFixed(2)); // 判斷是否為整數
}

// 解析 DB recordTrail 日期字串（相容新舊格式）
// 新格式：CURRENT_TIMESTAMP → "2026-05-19T12:10:19.123456+00:00"（含時區，dayjs 直接解析）
// 舊格式：to_char(now(),...) → "2026-05-19 12:10:19"（純 UTC 字串，需補 'Z'）
export const parseRecordDate = (dateStr, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!dateStr) return null;
  const hasTimezone = dateStr.includes('T') || dateStr.includes('+') || dateStr.endsWith('Z');
  const parsed = dayjs(hasTimezone ? dateStr : dateStr + 'Z');
  return parsed.isValid() ? parsed.format(format) : null;
};

// 轉換電力方案
export const convertToSelectOptions = (timePrice) => {
  if(!timePrice) return;
  return Object.entries(timePrice)
  .map(([label, value]) => ({
    label: label,
    value: value
  }));
}